// Packages
import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Components
import useComponents  from '../../../views/components'

// Helpers
import useHelpers from "../../../helpers";

// Api
import useApi from "../../../api";

// Models
import useModels from "../../../models";

// Constants
import { PATHS_WITH_COMPRESSED_NAVBAR } from "../../../config/constants/toolsAI";

const useNavBar = () => {
  const navigation = useNavigate();
  const location = useLocation();
  const [statusNavBar, setStatusNavBar] = useState(true);
  const [elementActive, setElementActive] = useState("");
  const [navBarItems, setNavBarItems] = useState([]);
  const refreshAttempted = useRef(false); // Track if we've already attempted refresh

  const { useActions } = useApi();
  const { useNavBarActions, useAuthActions } = useActions();
  const { actGetNavBarOptions } = useNavBarActions();
  const { actRefreshPermissions } = useAuthActions();
  const { useQuickFunctions } = useHelpers();
  const { useToast } = useQuickFunctions();
  const { error } = useToast();

  const { Toast } = useComponents();

  // Get user permissions from Redux
  const { useSelectors } = useModels();
  const { useSelector, useAuthSelectors } = useSelectors();
  const { session } = useAuthSelectors();
  const { permissions, authToken, permissionsLoaded, rol } = useSelector(session);

  // Redux dispatch
  const { dispatch } = useActions();

  // Auto-fetch permissions - ALWAYS refresh on mount to get fresh data from server
  useEffect(() => {
    // Force refresh if user is authenticated and we haven't attempted yet
    // This ensures we always get fresh permissions from /api/user, overwriting any stale cached data
    if (authToken && !refreshAttempted.current) {
      refreshAttempted.current = true;
      dispatch(actRefreshPermissions());
    }
    
    // Reset refresh flag if user logs out
    if (!authToken) {
      refreshAttempted.current = false;
    }
  }, [authToken, dispatch, actRefreshPermissions]);

  useEffect(() => {
    if (!permissionsLoaded || !permissions || permissions.length === 0) {
      return;
    }
    
    const onSuccess = (data) => {
      setNavBarItems(data.data);
    }
    const onError = () => {
      error(<Toast text={"Error al cargar los items del menu"} />, {
        toastId: 0,
      });
    }
    const fetchNavBarItems = actGetNavBarOptions(permissions || [], rol, onSuccess, onError);

    fetchNavBarItems();
  }, [permissions, permissionsLoaded, rol]); // Re-fetch when permissions/role change or become loaded


  useEffect(() => {
    const currentPath = window.location.pathname;
    if (PATHS_WITH_COMPRESSED_NAVBAR.includes(currentPath)) {
      setStatusNavBar(false);
    }
  }, [window.location.pathname]);


  const handledButton = useCallback(() => {
    setStatusNavBar(!statusNavBar);
  }, [statusNavBar]);

  const handleClickElement = useCallback(
    (itemName) => {
      if (itemName === elementActive) {
        setElementActive(null);
        localStorage.setItem('activeElement', null);
      } else {
        setElementActive(itemName);
        localStorage.setItem('activeElement', itemName);
      }
    },
    [elementActive]
  );

  const handleNavigationToSubItems = ({ url, optionSuboption }, parentItem) => {
    if (optionSuboption?.length) {
      navigation("/sub-items", { state: { optionSuboption } });
    } else {
      url.startsWith("http")
        ? window.open(url, '_blank')
        : navigation(url);

      // Set parent as active when navigating to subitem
      if (parentItem) {
        setElementActive(parentItem.name);
        localStorage.setItem('activeElement', parentItem.name);
      }
    }
  };


  const handleNavigateTo = (item) => {
    if (!item.optionSuboption.length) {
      item.url.includes("http")
        ? window.open(item.url, '_blank')
        : navigation(item.url);

      localStorage.setItem('activeElement', item.name);
    }
  };


  // Detect active parent based on current URL whenever location changes
  useEffect(() => {
    if (navBarItems.length === 0) return;

    const currentPath = location.pathname;

    // Find the parent item that matches the current path
    const activeParent = navBarItems.find(item => {
      // Check if current path starts with the item's URL
      if (item.url && item.url !== '/' && currentPath.startsWith(item.url)) {
        return true;
      }
      // Check if any subitem's URL matches
      return item.optionSuboption?.some(subItem =>
        subItem.url && subItem.url !== '/' && currentPath.startsWith(subItem.url)
      );
    });

    if (activeParent && activeParent.name !== elementActive) {
      setElementActive(activeParent.name);
      localStorage.setItem('activeElement', activeParent.name);
    }
  }, [location.pathname, navBarItems]);

  return {
    statusNavBar,
    handledButton,
    navBarItems,
    elementActive,
    handleClickElement,
    handleNavigationToSubItems,
    handleNavigateTo,
    currentPath: location.pathname,
  };
};

export default useNavBar;
